import { c as _c } from "react-compiler-runtime";
import { useEvent } from "@react-aria/utils";
import { useControlledState } from "@react-stately/utils";
import * as React from "react";
import { mergeProps } from "react-aria";
import { Button as AriaButton, ButtonContext, Select as AriaSelect, SelectContext, SelectValue as AriaSelectValue, useSlottedContext } from "react-aria-components";
import { ListBox } from "./ListBox.js";
import { Popover } from "./Popover.js";
import { selectValue } from "./SingleSelect.css.js";
import { FCTextFieldContext } from "./TextFieldContext.js";
import { ChevronButton } from "./shared/ChevronButton.js";
import { ClearButton } from "./shared/ClearButton.js";
import { InputBuilder } from "./shared/InputBuilder.js";
import { extractInputBuilderProps } from "./shared/inputBuilderHelper.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const DropdownValue = t0 => {
  const $ = _c(3);
  const {
    size,
    placeholder
  } = t0;
  let t1;
  if ($[0] !== placeholder || $[1] !== size) {
    t1 = /*#__PURE__*/_jsx(AriaSelectValue, {
      children: t2 => {
        const {
          selectedText,
          isPlaceholder
        } = t2;
        return /*#__PURE__*/_jsx("span", {
          className: selectValue({
            size,
            isPlaceholder
          }),
          children: isPlaceholder ? placeholder : selectedText
        });
      }
    });
    $[0] = placeholder;
    $[1] = size;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function Select(t0) {
  const $ = _c(21);
  const {
    triggerRef,
    buttonRef,
    onOpenChange,
    ...props
  } = t0;
  const {
    inputBuilderProps,
    restProps
  } = extractInputBuilderProps(props);
  const selectProps = useSlottedContext(SelectContext);
  const buttonProps = useSlottedContext(ButtonContext);
  const {
    textFieldProps,
    name
  } = React.useContext(FCTextFieldContext);
  const T0 = AriaSelect;
  const t1 = mergeProps(selectProps, restProps);
  let t2;
  if ($[0] !== inputBuilderProps.size || $[1] !== props.placeholder) {
    t2 = /*#__PURE__*/_jsx(DropdownValue, {
      size: inputBuilderProps.size,
      placeholder: props.placeholder
    });
    $[0] = inputBuilderProps.size;
    $[1] = props.placeholder;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== buttonProps || $[4] !== buttonRef || $[5] !== t2) {
    t3 = /*#__PURE__*/_jsx(AriaButton, {
      ...buttonProps,
      ref: buttonRef,
      children: t2
    });
    $[3] = buttonProps;
    $[4] = buttonRef;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== inputBuilderProps.size || $[8] !== props.children) {
    t4 = /*#__PURE__*/_jsx(ListBox, {
      size: inputBuilderProps.size,
      children: props.children
    });
    $[7] = inputBuilderProps.size;
    $[8] = props.children;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4 || $[11] !== triggerRef) {
    t5 = /*#__PURE__*/_jsx(Popover, {
      triggerRef: triggerRef,
      children: t4
    });
    $[10] = t4;
    $[11] = triggerRef;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== T0 || $[14] !== name || $[15] !== onOpenChange || $[16] !== t1 || $[17] !== t3 || $[18] !== t5 || $[19] !== textFieldProps) {
    t6 = /*#__PURE__*/_jsxs(T0, {
      name: name,
      ...textFieldProps,
      onOpenChange: onOpenChange,
      ...t1,
      children: [t3, t5]
    });
    $[13] = T0;
    $[14] = name;
    $[15] = onOpenChange;
    $[16] = t1;
    $[17] = t3;
    $[18] = t5;
    $[19] = textFieldProps;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
export function SingleSelect(t0) {
  const $ = _c(30);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    t1 = extractInputBuilderProps(props);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const {
    inputBuilderProps,
    restProps
  } = t1;
  const fcLabelProps = React.useContext(FCTextFieldContext);
  const buttonRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  let t2;
  if ($[3] !== fcLabelProps.labelRef) {
    t2 = fcLabelProps.labelRef ?? {
      current: null
    };
    $[3] = fcLabelProps.labelRef;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      e.stopPropagation();
      buttonRef.current?.focus();
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEvent(t2, "click", t3);
  const [isOpen, setIsOpen] = useControlledState(props.isOpen, false, props.onOpenChange);
  const [value, setValue] = useControlledState(props.value, props.defaultValue ?? null, props.onChange);
  let t4;
  if ($[6] !== isOpen || $[7] !== props.isRequired || $[8] !== setValue || $[9] !== value) {
    t4 = props.isRequired === true ? null : /*#__PURE__*/_jsx(ClearButton, {
      clearButton: true,
      isOpen: isOpen,
      hasSelection: value != null,
      onClear: () => {
        setValue(null);
      }
    });
    $[6] = isOpen;
    $[7] = props.isRequired;
    $[8] = setValue;
    $[9] = value;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isOpen || $[12] !== setIsOpen) {
    t5 = () => {
      setIsOpen(!isOpen);
    };
    $[11] = isOpen;
    $[12] = setIsOpen;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== isOpen || $[15] !== t5) {
    t6 = /*#__PURE__*/_jsx(ChevronButton, {
      element: "div",
      isOpen: isOpen,
      onClick: t5
    });
    $[14] = isOpen;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t4 || $[18] !== t6) {
    t7 = /*#__PURE__*/_jsxs(_Fragment, {
      children: [t4, t6]
    });
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== isOpen || $[21] !== restProps || $[22] !== setIsOpen || $[23] !== setValue || $[24] !== value) {
    t8 = /*#__PURE__*/_jsx(Select, {
      buttonRef: buttonRef,
      triggerRef: triggerRef,
      ...restProps,
      isOpen: isOpen,
      onOpenChange: setIsOpen,
      value: value,
      onChange: setValue
    });
    $[20] = isOpen;
    $[21] = restProps;
    $[22] = setIsOpen;
    $[23] = setValue;
    $[24] = value;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== inputBuilderProps || $[27] !== t7 || $[28] !== t8) {
    t9 = /*#__PURE__*/_jsx(InputBuilder, {
      ref: triggerRef,
      ...inputBuilderProps,
      right: t7,
      children: t8
    });
    $[26] = inputBuilderProps;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
//# sourceMappingURL=SingleSelect.js.map